import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../utils/log';

const log = logger('camera');

export type CameraFacingMode = 'user' | 'environment';
export type CameraStatus =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'ready' }
  | { kind: 'denied' }
  | { kind: 'error'; message: string };

export interface UseCameraOptions {
  /** Include an audio track in the stream (used for video recording). */
  audio?: boolean;
  /** Defaults to 'user' (front-facing). */
  facingMode?: CameraFacingMode;
}

export interface UseCameraApi {
  stream: MediaStream | null;
  status: CameraStatus;
  facingMode: CameraFacingMode;
  devices: MediaDeviceInfo[];
  attachVideo(el: HTMLVideoElement): void;
  flip(): Promise<void>;
  selectDevice(deviceId: string): Promise<void>;
  retry(): Promise<void>;
  stop(): void;
}

async function listVideoDevices(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'videoinput');
  } catch {
    return [];
  }
}

export function useCamera(opts: UseCameraOptions = {}): UseCameraApi {
  const wantAudio = !!opts.audio;
  const [facingMode, setFacingMode] = useState<CameraFacingMode>(
    opts.facingMode ?? 'user',
  );
  const [status, setStatus] = useState<CameraStatus>({ kind: 'idle' });
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const videoRefs = useRef<Set<HTMLVideoElement>>(new Set());
  const currentDeviceId = useRef<string | null>(null);

  const stop = useCallback(() => {
    setStream((s) => {
      s?.getTracks().forEach((t) => t.stop());
      return null;
    });
    setStatus({ kind: 'idle' });
  }, []);

  const startWith = useCallback(
    async (constraints: MediaStreamConstraints) => {
      log.info('getUserMedia →', constraints);
      setStatus({ kind: 'requesting' });
      try {
        const next = await navigator.mediaDevices.getUserMedia(constraints);
        const track = next.getVideoTracks()[0];
        log.info('stream acquired', {
          tracks: next.getTracks().map((t) => ({ kind: t.kind, label: t.label })),
          settings: track?.getSettings(),
        });
        setStream((prev) => {
          prev?.getTracks().forEach((t) => t.stop());
          return next;
        });
        for (const el of videoRefs.current) {
          el.srcObject = next;
          el.play().catch((err) => log.warn('video play() rejected', err));
        }
        setStatus({ kind: 'ready' });
        listVideoDevices().then(setDevices).catch(() => undefined);
      } catch (err: unknown) {
        const name = (err as DOMException)?.name ?? '';
        log.error('getUserMedia failed', name, err);
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setStatus({ kind: 'denied' });
        } else {
          setStatus({
            kind: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    },
    [],
  );

  const start = useCallback(
    async (mode: CameraFacingMode, deviceId?: string) => {
      const video: MediaTrackConstraints = deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: { ideal: mode } };
      // Aim for 720p — good enough for shareable photos, friendly to mobile GPUs.
      video.width = { ideal: 1280 };
      video.height = { ideal: 720 };
      // Ask the sensor for 30fps. Without this constraint, browsers often let
      // the sensor drop to 15fps in low light to lengthen exposure.
      video.frameRate = { ideal: 30, min: 15 };
      currentDeviceId.current = deviceId ?? null;
      await startWith({ video, audio: wantAudio });
    },
    [startWith, wantAudio],
  );

  // Initial start.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await start(facingMode);
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally don't include `start` or `facingMode` — flips go through
    // the dedicated `flip()` to avoid restart loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attachVideo = useCallback(
    (el: HTMLVideoElement) => {
      videoRefs.current.add(el);
      if (stream) {
        el.srcObject = stream;
        el.play().catch(() => undefined);
      }
    },
    [stream],
  );

  const flip = useCallback(async () => {
    const next: CameraFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    await start(next);
  }, [facingMode, start]);

  const selectDevice = useCallback(
    async (deviceId: string) => {
      await start(facingMode, deviceId);
    },
    [facingMode, start],
  );

  const retry = useCallback(async () => {
    await start(facingMode, currentDeviceId.current ?? undefined);
  }, [facingMode, start]);

  return { stream, status, facingMode, devices, attachVideo, flip, selectDevice, retry, stop };
}
